import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { useCart } from "../../context/CartContext";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { showError, showSuccess } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { color } from "framer-motion";

function Card({ item }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { config } = useSiteConfig();

  const initialPrice =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.price ?? 0)
      : (item?.price ?? 0);

  const initialMrp =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.mrp ?? 0)
      : (item?.mrp ?? 0);

  const initialDiscount =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.discount ?? 0)
      : (item?.discount ?? 0);

  const initialStock =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.stock ?? 0)
      : (item?.stock ?? 0);

  const [image] = useState(item?.thumbnail);
  const [slug] = useState(item?.slug);
  const [title] = useState(item?.title);

  const [activePrice, setActivePrice] = useState(initialPrice);
  const [activeMrp, setActiveMrp] = useState(initialMrp);
  const [activeDiscount, setActiveDiscount] = useState(initialDiscount);
  const [activeStock, setActiveStock] = useState(initialStock);
  const [activeVariantId] = useState(
    item?.productType === "Variant" && item?.variants?.length
      ? item?.variants[0]._id
      : null,
  );
  const [isWishlisted, setIsWishlisted] = useState(item.isWishlisted || false);

  useEffect(() => {
    if (item?.productType !== "Variant") return;
    const selected = item?.variants?.find((v) => v._id === activeVariantId);
    if (selected) {
      setActivePrice(selected.price);
      setActiveMrp(selected.mrp);
      setActiveDiscount(selected.discount);
      setActiveStock(selected.stock);
    }
  }, [activeVariantId, item]);

  const hasDiscount =
    activeDiscount !== null && activeDiscount > 0 && activeMrp > activePrice;

  const isOutOfStock = activeStock <= 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    try {
      addToCart(item?._id, 1, activeVariantId || "");
      //  
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: item?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  return (
    <div className="custom-product-card">
      {/* Image Wrapper */}
      <div className="custom-product-img-wrapper">
        {hasDiscount && (
          <span className="custom-product-tag">
            {Math.round(activeDiscount)}% OFF
          </span>
        )}

        <button
          className={`custom-product-wishlist ${isWishlisted ? "active" : ""}`}
          onClick={handleWishlistToggle}
          aria-label="Add to wishlist"
        >
          <Heart size={16} fill={isWishlisted ? "currentColor" : "none"} />
        </button>

        <Link to={`/shop/${slug}`} className="custom-product-img">
          <img src={image} alt={title} loading="lazy" />
        </Link>

        {isOutOfStock ? (
          <span className="custom-product-oos">Out of Stock</span>
        ) : (
          config?.productVisibility === "cart" && (
            <button
              className="custom-product-cart-btn"
              onClick={handleAddToCart}
              aria-label="Add to cart"
            >
              <ShoppingBag size={18} />
            </button>
          )
        )}
      </div>

      {/* Content */}
      <div className="custom-product-info">
        <div className="custom-product-title-row">
          <Link
            to={`/shop/${slug}`}
            className="custom-product-name text-decoration-none"
          >
            {title}
          </Link>

          {item?.rating > 0 && (
            <span className="custom-product-rating">
              <Star size={13} fill="currentColor" strokeWidth={0} />
              {item.rating.toFixed(1)}
            </span>
          )}
        </div>

        <div className="custom-product-price-row">
          <span className="custom-product-price">₹{activePrice}</span>
          {hasDiscount && (
            <>
              <span className="custom-product-price-del">₹{activeMrp}</span>
              <span className="custom-product-discount-pill text-light"  >
                {Math.round(activeDiscount)}% OFF
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Card;